import React from 'react';
import Checkbox from '@mui/material/Checkbox';
import { useFormikContext } from 'formik';
import {
  hasAnyYesAnswer,
  INFECTION_RISK_YES_NO_FIELDS,
  MEDICAL_IMPLANT_YES_NO_FIELDS,
  PreScanQuestionnaireFormData,
  PreScanQuestionnaireFormValues,
} from '../../../types/preScanQuestionnaire';
import InputLabelComp from '../InputLabelComp';
import PostalAddressField from '../PostalAddressField';
import YesNoRadioGroup from '../YesNoRadioGroup';
import { getPreScanQuestionnaireDisclaimerItems } from '../../../utils/preScanQuestionnaireDisplay';


interface PreScanQuestionnaireFormFieldsProps {
  fullName: string;
  dateOfBirth: string;
  gender: string;
  phoneNumber: string;
  email: string;
}

const PreScanQuestionnaireFormFields: React.FC<PreScanQuestionnaireFormFieldsProps> = ({
  fullName,
  dateOfBirth,
  gender,
  phoneNumber,
  email,
}) => {
  const { values, errors, submitCount, setFieldValue, setFieldError, isSubmitting, handleSubmit } =
    useFormikContext<PreScanQuestionnaireFormValues>();

  const getFieldError = (field: keyof PreScanQuestionnaireFormValues) =>
    submitCount > 0 && errors[field] ? String(errors[field]) : undefined;

  const getDisclaimerError = (item: string) =>
    submitCount > 0 &&
    errors.disclaimer_confirmations &&
    typeof errors.disclaimer_confirmations === 'object' &&
    errors.disclaimer_confirmations[item]
      ? String(errors.disclaimer_confirmations[item])
      : undefined;

  const setValueAndClearError = (
    field: keyof PreScanQuestionnaireFormValues,
    value: string
  ) => {
    setFieldValue(field, value);
    if (submitCount > 0 && errors[field]) {
      setFieldError(field, undefined);
    }
  };

  const clearFieldError = (field: keyof PreScanQuestionnaireFormValues) => {
    if (submitCount > 0 && errors[field]) {
      setFieldError(field, undefined);
    }
  };

  const setYesNoWithDetails = (
    yesNoField: keyof PreScanQuestionnaireFormValues,
    detailsField: keyof PreScanQuestionnaireFormValues,
    value: string
  ) => {
    setValueAndClearError(yesNoField, value);
    if (value === 'No') {
      setFieldValue(detailsField, '');
      clearFieldError(detailsField);
    }
  };

  const setGroupedYesNo = (
    field: keyof PreScanQuestionnaireFormValues,
    value: string,
    groupFields: Array<keyof PreScanQuestionnaireFormData>,
    detailsField: keyof PreScanQuestionnaireFormValues
  ) => {
    setValueAndClearError(field, value);
    const updatedValues = { ...values, [field]: value };
    if (!hasAnyYesAnswer(updatedValues, groupFields)) {
      clearFieldError(detailsField);
    }
  };

  const metallicImplantsDetailsRequired = hasAnyYesAnswer(
    values,
    MEDICAL_IMPLANT_YES_NO_FIELDS
  );
  const infectionAllergyDetailsRequired = hasAnyYesAnswer(values, INFECTION_RISK_YES_NO_FIELDS);

  return (
    <form noValidate onSubmit={handleSubmit}>
      <div className='personal-information-wrapper'>
        <div className='key-value-wrap'>
          <span className='key'>Full name</span>
          <span className='value'>{fullName}</span>
        </div>
        <div className='key-value-wrap'>
          <span className='key'>Date of Birth</span>
          <span className='value'>{dateOfBirth}</span>
        </div>
        <div className='key-value-wrap'>
          <span className='key'>Gender</span>
          <span className='value'>{gender}</span>
        </div>
        <div className='key-value-wrap'>
          <span className='key'>Phone number</span>
          <span className='value'>{phoneNumber}</span>
        </div>
        <div className='key-value-wrap'>
          <span className='key'>Email</span>
          <span className='value'>{email}</span>
        </div>
      </div>
      <div className='bottom-space-40'>
        <PostalAddressField
          value={values.center_address}
          onChange={(value) => setValueAndClearError('center_address', value)}
          error={getFieldError('center_address')}
        />
      </div>
      <div className='bottom-space-40'>
        <label className='label-style'> Medical History</label>
        <YesNoRadioGroup
          label='Do you have any past or ongoing medical conditions we should know about?'
          required
          name='past-ongoing-medical-conditions'
          showMoreDetails
          value={values.past_ongoing_medical_conditions}
          onChange={(value) =>
            setYesNoWithDetails(
              'past_ongoing_medical_conditions',
              'past_ongoing_medical_conditions_details',
              value
            )
          }
          detailsValue={values.past_ongoing_medical_conditions_details}
          onDetailsChange={(value) =>
            setValueAndClearError('past_ongoing_medical_conditions_details', value)
          }
          error={getFieldError('past_ongoing_medical_conditions')}
          detailsError={getFieldError('past_ongoing_medical_conditions_details')}
        />
        <YesNoRadioGroup
          label='Have you had any previous surgeries or medical procedures?'
          required
          name='previous-surgeries-medical-procedures'
          showMoreDetails
          value={values.any_surgery_past_3_month}
          onChange={(value) =>
            setYesNoWithDetails(
              'any_surgery_past_3_month',
              'any_surgery_past_3_month_details',
              value
            )
          }
          detailsValue={values.any_surgery_past_3_month_details}
          onDetailsChange={(value) =>
            setValueAndClearError('any_surgery_past_3_month_details', value)
          }
          error={getFieldError('any_surgery_past_3_month')}
          detailsError={getFieldError('any_surgery_past_3_month_details')}
        />
        <YesNoRadioGroup
          label='Are you currently taking any medications or supplements?'
          required
          name='medications-supplements'
          showMoreDetails
          value={values.medications_supplements}
          onChange={(value) =>
            setYesNoWithDetails('medications_supplements', 'medications_supplements_details', value)
          }
          detailsValue={values.medications_supplements_details}
          onDetailsChange={(value) =>
            setValueAndClearError('medications_supplements_details', value)
          }
          error={getFieldError('medications_supplements')}
          detailsError={getFieldError('medications_supplements_details')}
        />
      </div>
      <div className='bottom-space-40'>
        <label className='label-style'> Cancer History</label>
        <YesNoRadioGroup
          label='Have you ever been diagnosed with cancer?'
          required
          name='cancer-history'
          showMoreDetails
          value={values.cancer_history}
          onChange={(value) =>
            setYesNoWithDetails('cancer_history', 'cancer_history_details', value)
          }
          detailsValue={values.cancer_history_details}
          onDetailsChange={(value) => setValueAndClearError('cancer_history_details', value)}
          error={getFieldError('cancer_history')}
          detailsError={getFieldError('cancer_history_details')}
        />
        <YesNoRadioGroup
          label='Have you had any previous medical imaging such as an MRI, CT, or X-ray?'
          required
          name='previous-medical-imaging'
          showMoreDetails
          value={values.any_previous_imaging}
          onChange={(value) =>
            setYesNoWithDetails('any_previous_imaging', 'any_previous_imaging_details', value)
          }
          detailsValue={values.any_previous_imaging_details}
          onDetailsChange={(value) => setValueAndClearError('any_previous_imaging_details', value)}
          error={getFieldError('any_previous_imaging')}
          detailsError={getFieldError('any_previous_imaging_details')}
        />
      </div>
      <div className='bottom-space-40'>
        <label className='label-style'> Clinical Concern</label>
        <InputLabelComp
          label='What is the primary reason for booking a full body MRI scan with us?'
          isRequired
          value={values.reason_for_scan}
          onChange={(value) => setValueAndClearError('reason_for_scan', value)}
          error={getFieldError('reason_for_scan')}
        />
        <InputLabelComp
          label='Are you currently experiencing any symptoms? (e.g. pain, discomfort etc.)'
          isRequired
          value={values.current_symptoms}
          onChange={(value) => setValueAndClearError('current_symptoms', value)}
          error={getFieldError('current_symptoms')}
        />
      </div>
      <div className='gray-card-outer'>
        <label className='label-style'> Medical Implants and Metallic Objects</label>
        <YesNoRadioGroup
          label='Any known metallic piercing/object/shrapnel'
          required
          name='metallic-piercing-object-shrapnel'
          value={values.metallic_piercing_object_shrapnel}
          onChange={(value) =>
            setGroupedYesNo(
              'metallic_piercing_object_shrapnel',
              value,
              MEDICAL_IMPLANT_YES_NO_FIELDS,
              'metallic_implants_details'
            )
          }
          error={getFieldError('metallic_piercing_object_shrapnel')}
        />
        <YesNoRadioGroup
          label='Cardiac pacemaker or defibrillator, ICD, cardiac stent or metallic heart valve'
          required
          name='pacemaker-defibrillator'
          value={values.pacemaker_defibrillator}
          onChange={(value) =>
            setGroupedYesNo(
              'pacemaker_defibrillator',
              value,
              MEDICAL_IMPLANT_YES_NO_FIELDS,
              'metallic_implants_details'
            )
          }
          error={getFieldError('pacemaker_defibrillator')}
        />
        <YesNoRadioGroup
          label='Metalwork/clips/coils after fractures or surgery'
          required
          name='metalwork-fractures-surgery'
          value={values.metalwork_fractures_surgery}
          onChange={(value) =>
            setGroupedYesNo(
              'metalwork_fractures_surgery',
              value,
              MEDICAL_IMPLANT_YES_NO_FIELDS,
              'metallic_implants_details'
            )
          }
          error={getFieldError('metalwork_fractures_surgery')}
        />
        <YesNoRadioGroup
          label='Any joint or limb replacement eg hip/knee'
          required
          name='joint-limb-replacement'
          value={values.joint_limb_replacement}
          onChange={(value) =>
            setGroupedYesNo(
              'joint_limb_replacement',
              value,
              MEDICAL_IMPLANT_YES_NO_FIELDS,
              'metallic_implants_details'
            )
          }
          error={getFieldError('joint_limb_replacement')}
        />
        <YesNoRadioGroup
          label='Contraceptive coil (IUD)'
          required
          name='contraceptive-coil'
          value={values.contraceptive_coil}
          onChange={(value) =>
            setGroupedYesNo(
              'contraceptive_coil',
              value,
              MEDICAL_IMPLANT_YES_NO_FIELDS,
              'metallic_implants_details'
            )
          }
          error={getFieldError('contraceptive_coil')}
        />
        <YesNoRadioGroup
          label='Internal hearing devices or cochlear implants'
          required
          name='hearing-devices'
          value={values.hearing_devices}
          onChange={(value) =>
            setGroupedYesNo(
              'hearing_devices',
              value,
              MEDICAL_IMPLANT_YES_NO_FIELDS,
              'metallic_implants_details'
            )
          }
          error={getFieldError('hearing_devices')}
        />
        <YesNoRadioGroup
          label='Hydrocephalus shunt, spinal shunt or neurostimulator'
          required
          name='hydrocephalus-shunt'
          value={values.hydrocephalus_shunt}
          onChange={(value) =>
            setGroupedYesNo(
              'hydrocephalus_shunt',
              value,
              MEDICAL_IMPLANT_YES_NO_FIELDS,
              'metallic_implants_details'
            )
          }
          error={getFieldError('hydrocephalus_shunt')}
        />
        <YesNoRadioGroup
          label='Any dental implants, fillings, dentures or devices'
          required
          name='dental-implants'
          value={values.dental_implants}
          onChange={(value) =>
            setGroupedYesNo(
              'dental_implants',
              value,
              MEDICAL_IMPLANT_YES_NO_FIELDS,
              'metallic_implants_details'
            )
          }
          error={getFieldError('dental_implants')}
        />
        <YesNoRadioGroup
          label='Any metallic objects or shrapnels in the eye'
          required
          name='metallic-objects-shrapnel-eye'
          value={values.metallic_objects_shrapnel_eye}
          onChange={(value) =>
            setGroupedYesNo(
              'metallic_objects_shrapnel_eye',
              value,
              MEDICAL_IMPLANT_YES_NO_FIELDS,
              'metallic_implants_details'
            )
          }
          error={getFieldError('metallic_objects_shrapnel_eye')}
        />
        <YesNoRadioGroup
          label='Specific types of aneurysm clips'
          required
          name='aneurysm-clips'
          value={values.aneurysm_clips}
          onChange={(value) =>
            setGroupedYesNo(
              'aneurysm_clips',
              value,
              MEDICAL_IMPLANT_YES_NO_FIELDS,
              'metallic_implants_details'
            )
          }
          error={getFieldError('aneurysm_clips')}
        />
      </div>
      <div
        className={`more-details-wrapper bottom-space-40 ${getFieldError('metallic_implants_details') ? 'error' : ''}`}
      >
        <label className='label-style'>
          If you selected yes on anything, please give details.
          {metallicImplantsDetailsRequired && <span className='required'>*</span>}
        </label>
        <div className='label-sub-style'>
          Type, location in your body, and when it was inserted.
        </div>
        <textarea
          className='textarea-style'
          rows={1}
          placeholder='Provide details here'
          value={values.metallic_implants_details}
          onChange={(e) => setValueAndClearError('metallic_implants_details', e.target.value)}
        />
        {getFieldError('metallic_implants_details') && (
          <div className='error-text-message'>{getFieldError('metallic_implants_details')}</div>
        )}
      </div>
      <div className='gray-card-outer'>
        <label className='label-style'> Infection Risks or Allergies</label>
        <YesNoRadioGroup
          label='Do you have any infection risk (e.g. MRSA)?'
          required
          name='infection-risk'
          value={values.infection_risk}
          onChange={(value) =>
            setGroupedYesNo(
              'infection_risk',
              value,
              INFECTION_RISK_YES_NO_FIELDS,
              'infection_allergy_details'
            )
          }
          error={getFieldError('infection_risk')}
        />
        <YesNoRadioGroup
          label='Are you diabetic?'
          required
          name='diabetic'
          value={values.diabetic}
          onChange={(value) =>
            setGroupedYesNo('diabetic', value, INFECTION_RISK_YES_NO_FIELDS, 'infection_allergy_details')
          }
          error={getFieldError('diabetic')}
        />
        <YesNoRadioGroup
          label='Any special needs eg hearing, mobility, speech or other?'
          required
          name='special-needs'
          value={values.special_needs}
          onChange={(value) =>
            setGroupedYesNo(
              'special_needs',
              value,
              INFECTION_RISK_YES_NO_FIELDS,
              'infection_allergy_details'
            )
          }
          error={getFieldError('special_needs')}
        />
        <YesNoRadioGroup
          label='Any claustrophobia?'
          required
          name='claustrophobia'
          value={values.claustrophobia}
          onChange={(value) =>
            setGroupedYesNo(
              'claustrophobia',
              value,
              INFECTION_RISK_YES_NO_FIELDS,
              'infection_allergy_details'
            )
          }
          error={getFieldError('claustrophobia')}
        />
        <YesNoRadioGroup
          label='Do you have any allergies?'
          required
          name='any_allergies'
          value={values.any_allergies}
          onChange={(value) =>
            setGroupedYesNo(
              'any_allergies',
              value,
              INFECTION_RISK_YES_NO_FIELDS,
              'infection_allergy_details'
            )
          }
          error={getFieldError('any_allergies')}
        />
      </div>
      <div className={getFieldError('infection_allergy_details') ? 'error' : ''}>
        <label className='label-style'>
          If you selected yes on anything, please provide details.
          {infectionAllergyDetailsRequired && <span className='required'>*</span>}
        </label>
        <textarea
          className='textarea-style'
          rows={1}
          placeholder='Provide details here'
          value={values.infection_allergy_details}
          onChange={(e) => setValueAndClearError('infection_allergy_details', e.target.value)}
        />
        {getFieldError('infection_allergy_details') && (
          <div className='error-text-message'>{getFieldError('infection_allergy_details')}</div>
        )}
      </div>
      <div className='confirm-information-checkboxes'>
        {getPreScanQuestionnaireDisclaimerItems(gender).map((item, index) => {
          const checkboxId = `disclaimer-confirmation-${index}`;
          return (
            <div
              key={item}
              className={`confirm-information-checkbox ${getDisclaimerError(item) ? 'error' : ''}`}
            >
              <div className='d-flex align-items-center'>
                <Checkbox
                  id={checkboxId}
                  checked={values.disclaimer_confirmations[item] || false}
                  onChange={(e) => {
                    setFieldValue('disclaimer_confirmations', {
                      ...values.disclaimer_confirmations,
                      [item]: e.target.checked,
                    });
                    if (submitCount > 0 && errors.disclaimer_confirmations) {
                      setFieldError('disclaimer_confirmations', undefined);
                    }
                  }}
                />
                <label htmlFor={checkboxId}>
                  {item}
                  <span className='required'>*</span>
                </label>
              </div>
              {getDisclaimerError(item) && (
                <div className='error-text-message'>{getDisclaimerError(item)}</div>
              )}
            </div>
          );
        })}
      </div>
      <button className='main-button' type='submit' disabled={isSubmitting}>
        {isSubmitting ? 'Submitting...' : 'Confirm completion'}
      </button>
    </form>
  );
};

export default PreScanQuestionnaireFormFields;
